import { constructOrgTargetId, type FeatureFlagKey, TARGET_ORG } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import {
  getOrgFeatureFlagsQueryKey,
  type OrgFeatureFlagsResponse,
} from './get-org-feature-flags-admin';

import type { FlagTargetState } from '@nohara-dxdev/feature-flags';

export interface ToggleOrgFeatureFlagInput {
  flagKey: FeatureFlagKey;
  enabled: boolean;
}

interface OptimisticContext {
  previous: OrgFeatureFlagsResponse | undefined;
}

const toggleOrgFeatureFlag = async (
  orgId: string,
  input: ToggleOrgFeatureFlagInput,
): Promise<{ success: boolean }> => {
  const { data } = await api.put<unknown, ApiResponse<{ success: boolean }>>(
    `/orgs/${orgId}/feature-flags/${input.flagKey}`,
    { enabled: input.enabled },
  );
  return data;
};

const upsertOrgRow = (
  current: OrgFeatureFlagsResponse | undefined,
  orgTargetId: string,
  input: ToggleOrgFeatureFlagInput,
): OrgFeatureFlagsResponse => {
  const others = (current?.flags ?? []).filter(
    (s) =>
      !(s.flagKey === input.flagKey && s.targetType === TARGET_ORG && s.targetId === orgTargetId),
  );
  const next: FlagTargetState = {
    flagKey: input.flagKey,
    targetType: TARGET_ORG,
    targetId: orgTargetId,
    enabled: input.enabled,
    updatedAt: Date.now(),
    updatedBy: 'optimistic',
  };
  return { flags: [...others, next] };
};

interface UseToggleOrgFeatureFlagOptions {
  mutationConfig?: MutationConfig<
    (input: ToggleOrgFeatureFlagInput) => Promise<{ success: boolean }>
  >;
}

export const useToggleOrgFeatureFlag = ({
  mutationConfig,
}: UseToggleOrgFeatureFlagOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId } = useScopeIds('org');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation<{ success: boolean }, Error, ToggleOrgFeatureFlagInput, OptimisticContext>({
    ...restConfig,
    onMutate: async (input) => {
      const queryKey = getOrgFeatureFlagsQueryKey(orgId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<OrgFeatureFlagsResponse>(queryKey);
      const orgTargetId = constructOrgTargetId(orgId);
      queryClient.setQueryData<OrgFeatureFlagsResponse>(queryKey, (old) =>
        upsertOrgRow(old, orgTargetId, input),
      );
      return { previous };
    },
    onSuccess: (data, vars, ctx, mutationContext) => {
      notifications.success('featureFlags.toggle.success');
      onSuccess?.(data, vars, ctx, mutationContext);
    },
    onError: (error, vars, ctx, mutationContext) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(getOrgFeatureFlagsQueryKey(orgId), ctx.previous);
      }
      notifications.error('featureFlags.toggle.error');
      onError?.(error, vars, ctx, mutationContext);
    },
    onSettled: () => {
      // Admin cache is patched optimistically — no refetch.
      // The `me` query drives FAB visibility across the app and is a separate concern.
      void queryClient.invalidateQueries({ queryKey: ['feature-flags', 'me', orgId] });
    },
    mutationFn: (input) => toggleOrgFeatureFlag(orgId, input),
  });
};
